import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { get, authGet } from '../../services/apiService';
import { isAuthenticated } from '../../services/authService';

const SnykPackageChart = ({ showUnresolved = true, outOfSLA = false, collection = '' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalOpenIssues, setTotalOpenIssues] = useState(0);
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortBy, setSortBy] = useState('percentage');
  const [commonLibraries, setCommonLibraries] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const loadedRef = useRef(false);
  const prevPropsRef = useRef({ showUnresolved, outOfSLA, collection });

  useEffect(() => {
    const propsChanged =
      prevPropsRef.current.showUnresolved !== showUnresolved ||
      prevPropsRef.current.outOfSLA !== outOfSLA ||
      prevPropsRef.current.collection !== collection;

    prevPropsRef.current = { showUnresolved, outOfSLA, collection };

    if (expanded && (propsChanged || !loadedRef.current)) {
      fetchData();
      fetchCommonLibraries();
    }
  }, [showUnresolved, outOfSLA, collection, expanded]);

  const fetchCommonLibraries = async () => {
    if (!isAuthenticated()) {
      // Skip fetching common libraries for unauthenticated users
      return;
    }
    
    try {
      const response = await authGet('/api/common-library');
      if (response && response.success && response.libraries) {
        const libraryNames = response.libraries.map(lib => lib.library);
        setCommonLibraries(libraryNames);
        console.log('Common libraries loaded:', libraryNames);
      }
    } catch (err) {
      console.error('Error fetching common libraries:', err);
      // Don't show error to user, just log it
    }
  };

  // Check if a package is part of common libraries
  const isCommonLibrary = (packageName) => {
    if (!packageName || commonLibraries.length === 0) return false;
    const isCommon = commonLibraries.includes(packageName);
    if (isCommon) {
      console.log('Common library found:', packageName);
    }
    return isCommon;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      loadedRef.current = true;
      const queryParams = new URLSearchParams();
      if (outOfSLA) {
        queryParams.append('outOfSLA', 'true');
      } else if (showUnresolved) {
        queryParams.append('showUnresolved', 'true');
      }
      if (collection) {
        queryParams.append('projectCollection', collection);
      }
      const queryString = queryParams.toString();
      const url = `/api/snyk/package-summary${queryString ? `?${queryString}` : ''}`;
      const response = await get(url);
      
      if (response && response.success && response.data) {
        const formattedData = response.data.map(item => ({
          ...item,
          percentage: parseFloat(item.percentage?.toFixed(2) || 0)
        }));
        setData(formattedData);
        setTotalOpenIssues(response.totalOpenIssues || 0);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching package summary:', err);
      setError(err.message || 'Failed to fetch package data');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  // Render a table for each chunk
  const renderTable = (chunk, title, chunkIndex) => (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }} variant="outlined">
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
        {title}
      </Typography>
      <TableContainer sx={{ flexGrow: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '40%' }}>
                <TableSortLabel
                  active={sortBy === 'packageName'}
                  direction={sortBy === 'packageName' ? sortOrder : 'asc'}
                  onClick={() => handleSort('packageName')}
                >
                  <strong>Package Name</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '35%' }}>
                <strong>Versions</strong>
              </TableCell>
              <TableCell align="right" sx={{ width: '25%' }}>
                <TableSortLabel
                  active={sortBy === 'percentage'}
                  direction={sortBy === 'percentage' ? sortOrder : 'asc'}
                  onClick={() => handleSort('percentage')}
                >
                  <strong>Percentage (%)</strong>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chunk.map((row, index) => {
              const isCommon = isCommonLibrary(row.packageName);
              return (
                <TableRow key={`chunk-${chunkIndex}-${index}`} hover>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    <Typography
                      component="span"
                      sx={{
                        color: isCommon ? '#1976d2' : 'inherit',
                        fontWeight: isCommon ? 600 : 'normal',
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      {row.packageName}
                    </Typography>
                    {row.fixedInVersions && row.fixedInVersions.length > 0 && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: '#2e7d32',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          display: 'block'
                        }}
                      >
                        Fixed in: {row.fixedInVersions.join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {row.versions && row.versions.length > 0 ? (
                      <Box>
                        {row.versions.map((version, vIndex) => (
                          <Typography
                            key={vIndex}
                            component="span"
                            variant="caption"
                            sx={{
                              display: 'inline-block',
                              mr: 0.5,
                              mb: 0.5,
                              px: 0.5,
                              py: 0.25,
                              bgcolor: 'rgba(0, 0, 0, 0.05)',
                              borderRadius: '4px'
                            }}
                          >
                            {version}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <strong>{row.percentage.toFixed(2)}%</strong>
                  </TableCell>
                </TableRow>
              );
            })}
            {chunk.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {chunk.length > 0 && (
              <TableRow 
                sx={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <TableCell colSpan={2}>
                  <strong>Total ({title})</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>
                    {chunk.reduce((sum, row) => sum + row.percentage, 0).toFixed(2)}%
                  </strong>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    if (!loadedRef.current) {
      return (
        <Typography variant="body2" color="text.secondary">
          Expand to load package data.
        </Typography>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No package data available
        </Typography>
      );
    }

    const sortedData = [...data].sort((a, b) => {
      const aValue = sortBy === 'packageName' 
        ? a.packageName.toLowerCase()
        : a[sortBy];
      const bValue = sortBy === 'packageName'
        ? b.packageName.toLowerCase()
        : b[sortBy];
      
      if (sortBy === 'packageName') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });

    // Split data into 2 chunks of 10
    const chunkSize = 10;
    const dataChunk1 = sortedData.slice(0, chunkSize);
    const dataChunk2 = sortedData.slice(chunkSize, chunkSize * 2);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {renderTable(dataChunk1, 'Top 10 Packages', 1)}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderTable(dataChunk2, 'Next 10 Packages', 2)}
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="package-contribution-content"
          id="package-contribution-header"
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="h6">
              Package Contribution to Open Issues
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Open Issues: {totalOpenIssues.toLocaleString()}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {renderContent()}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default SnykPackageChart;

